import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp, FiSearch } from "react-icons/fi";
import CustomTooltip from "./CustomTooltip";
import "./CustomerEntitySelect.css";

const normalizeText = (v) => String(v ?? "").trim();
const normalizeEntityText = (v) => {
  const text = normalizeText(v);
  if (!text) return "";

  const normalized = text.toLowerCase();
  if (
    normalized === "-" ||
    normalized === "_" ||
    normalized === "null" ||
    normalized === "undefined" ||
    normalized === "n/a"
  ) {
    return "";
  }

  return text;
};

const CustomerEntitySelect = ({
  value,
  onChange,
  options = [],
  width = "100%",
  placeholder = "Customer Name",
  disabled = false,
  searchThreshold = 5,
  noDataText = "No customers found",
  endAdornment = null,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [canScroll, setCanScroll] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [dropdownMaxHeight, setDropdownMaxHeight] = useState(360);
  const containerRef = useRef(null);
  const optionsRef = useRef(null);

  const normalizedOptions = useMemo(
    () =>
      Array.from(
        new Map(
          options.map((opt, index) => {
        const customerName = normalizeText(opt.customerName || opt.label || "-");
        const entityName = normalizeEntityText(
          opt.entityName ||
            opt.entity ||            
            "-"
        );
        const customerCode=normalizeText(opt.customerCode || "-");
        const searchText = normalizeText(
          opt.searchText || `${customerName} ${entityName} ${customerCode}`
        ).toLowerCase();

        const normalizedOption = {
          ...opt,
          value: normalizeText(opt.value),
          customerName,
          entityName,
          customerCode,
          searchText,
          optionKey: `${normalizeText(opt.value)}__${customerName}__${entityName}__${index}`,
        };

        const dedupeKey = `${normalizedOption.value}__${customerName}__${entityName}`;
        return [dedupeKey, normalizedOption];
          })
        ).values()
      ),
    [options]
  );

  const selected = normalizedOptions.find((o) => o.value === String(value ?? ""));

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return normalizedOptions;
    return normalizedOptions.filter((o) => o.searchText.includes(term));
  }, [normalizedOptions, searchTerm]);

  const hasSearch = normalizedOptions.length > searchThreshold;
  const optionsMaxHeight = Math.max(
    120,
    dropdownMaxHeight - (hasSearch ? 56 : 0) - (canScroll ? 32 : 0)
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = optionsRef.current;
    if (!el) return;

    const updateScrollState = () => {
      const scrollable = el.scrollHeight > el.clientHeight + 2;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
      setCanScroll(scrollable);
      setIsAtBottom(atBottom);
    };

    updateScrollState();
    const raf = window.requestAnimationFrame(updateScrollState);
    return () => window.cancelAnimationFrame(raf);
  }, [open, filteredOptions.length]);

  useEffect(() => {
    if (!open) return;

    const updateDropdownHeight = () => {
      const host = containerRef.current;
      if (!host) return;
      const rect = host.getBoundingClientRect();
      const viewportGap = 12;
      const availableBelow = window.innerHeight - rect.bottom - viewportGap;
      const bounded = Math.max(180, Math.min(420, availableBelow));
      setDropdownMaxHeight(bounded);
    };

    updateDropdownHeight();
    window.addEventListener("resize", updateDropdownHeight);
    window.addEventListener("scroll", updateDropdownHeight, true);
    return () => {
      window.removeEventListener("resize", updateDropdownHeight);
      window.removeEventListener("scroll", updateDropdownHeight, true);
    };
  }, [open]);

  return (
    <div className="customer-select" style={{ width }} ref={containerRef}>
      <button
        type="button"
        className={`customer-select-trigger ${disabled ? "is-disabled" : ""}`}
        onClick={() => !disabled && setOpen((prev) => !prev)}
      >
        <div className="customer-select-trigger-content">
          {selected ? (
            <CustomTooltip message={selected.customerName}>
              <p className="customer-select-name">{selected.customerName}</p>
            </CustomTooltip>
          ) : (
            <span className="customer-select-placeholder">{placeholder}</span>
          )}
        </div>
        <span className="customer-select-trigger-actions">
          {endAdornment ? (
            <span
              className="customer-select-adornment"
              onMouseDown={(event) => event.preventDefault()}
              onClick={(event) => event.stopPropagation()}
            >
              {endAdornment}
            </span>
          ) : null}
          <span className="customer-select-arrow">
            {open ? <FiChevronUp /> : <FiChevronDown />}
          </span>
        </span>
      </button>

      {open && !disabled && (
        <div
          className="customer-select-dropdown"
          style={{ maxHeight: `${dropdownMaxHeight}px` }}
        >
          {hasSearch && (
            <div className="customer-select-search">
              <FiSearch className="customer-select-search-icon" />
              <input
                type="text"
                placeholder="Search by customer or entity"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="customer-select-search-input"
                autoFocus
              />
            </div>
          )}

          <div
            className="customer-select-options"
            ref={optionsRef}
            style={{ maxHeight: `${optionsMaxHeight}px` }}
            onScroll={() => {
              const el = optionsRef.current;
              if (!el) return;
              const atBottom =
                el.scrollTop + el.clientHeight >= el.scrollHeight - 2;
              setIsAtBottom(atBottom);
            }}
          >
            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => (
                <button
                  type="button"
                  key={opt.optionKey}
                  className={`customer-select-option ${
                    opt.value === String(value ?? "") ? "is-selected" : ""
                  } ${opt.disabled ? "is-option-disabled" : ""}`}
                  disabled={opt.disabled}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange?.(opt.value);
                    setOpen(false);
                    setSearchTerm("");
                  }}
                >
                  <CustomTooltip message={opt.customerName}>
                    <p className="customer-select-option-name">{opt.customerName}</p>
                  </CustomTooltip>
                  {(opt.entityName && 
                    <div className="customer-select-option-footer">
                        <p className="customer-select-option-entity">{opt.entityName}</p>
                        <p className="customer-select-option-customer-code">
                          <CustomTooltip message={opt.customerCode}>{opt.customerCode}</CustomTooltip>
                        </p>
                    </div>
                  )}
                      
                </button>
              ))
            ) : (
              <p className="customer-select-no-data">{noDataText}</p>
            )}
          </div>

          {canScroll && (
            <div className="customer-select-scroll-status">
              {isAtBottom ? "End of results" : "Scroll down for more"}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerEntitySelect;
